import React from 'react';
import { Button } from '@/components/ui/button';

interface PaginationControlsProps {
  /** Current page index (0-based) */
  page: number;
  /** Items per page */
  pageSize: number;
  /** Total number of items */
  totalItems: number;
  /** Called with the new 0-based page index */
  onPageChange: (page: number) => void;
  /** Label for items, e.g. "insights", "flows" */
  itemLabel?: string;
  className?: string;
}

export function PaginationControls({
  page,
  pageSize,
  totalItems,
  onPageChange,
  itemLabel = 'items',
  className,
}: PaginationControlsProps) {
  if (totalItems <= pageSize) return null;

  const totalPages = Math.ceil(totalItems / pageSize);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const rangeStart = safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, totalItems);

  return (
    <div className={className ?? 'flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-4 pt-4 border-t'}>
      <div className="text-sm text-muted-foreground">
        Showing {rangeStart}-{rangeEnd} of {totalItems} {itemLabel}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(0, safePage - 1))}
          disabled={safePage === 0}
        >
          Previous
        </Button>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Page {safePage + 1} of {totalPages}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage + 1 >= totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
